import * as React from "react";
import Downshift from "downshift";
import { Div } from "@vebox/etui-system-elements";
import {
	AutoSuggestionItemProps,
	AutoSuggestProps,
	InputAddon,
	Spinner,
	SpinnerSize,
	TextInput,
	TextInputProps,
} from "@vebox/etui-core";
import * as S from "./styles";
import { StyleProps } from "@vebox/etui-system";
import { GroupedAutoSuggestion } from "./types";

interface VeBoxAutoSuggestProps {
	endInputAddon?: React.ReactNode;
	styles?: {
		root?: StyleProps;
		input?: StyleProps;
		suggestions?: StyleProps;
		label?: StyleProps;
	};
	size?: "small" | "default";
	closeOnLoading?: boolean;
	groupedSuggestions?: GroupedAutoSuggestion[];
	suggestions?: AutoSuggestionItemProps[];
	onSelectedSuggestion?: (suggestion: AutoSuggestionItemProps) => void;
	canOpen?: boolean;
}

const VeBoxAutoSuggest: React.FC<
	AutoSuggestProps & VeBoxAutoSuggestProps & TextInputProps
> = ({
	label = "Search",
	emptyPlaceholder = "No items found",
	styles,
	suggestions,
	onSelectedValueChange,
	onStateChange,
	onInputValueChange,
	itemToString,
	inputValue,
	isInputDisabled,
	selectedItem,
	itemRenderer,
	defaultIsOpen,
	initialIsOpen,
	openOnFocus,
	isLoading,
	stateReducer,
	excludeSelected,
	selectedItems,
	endInputAddon,
	closeOnLoading,
	groupedSuggestions,
	onSelectedSuggestion,
	size,
	canOpen,
}) => {
	const isGroupedSuggestions = React.useMemo(
		() => !!groupedSuggestions,
		[groupedSuggestions],
	);
	const items = isGroupedSuggestions ? groupedSuggestions : suggestions;
	const [highlightedItem, setHighlightedItem] =
		React.useState<AutoSuggestionItemProps>();

	const renderItems = (getItemProps, suggestionsList) => {
		if (itemRenderer) {
			return suggestionsList.map((item, index) =>
				React.cloneElement(
					itemRenderer(
						item,
						highlightedItem && highlightedItem?.value === item.value,
					),
					{
						...getItemProps({
							index,
							item,
							key: item.value,
						}),
					},
				),
			);
		}

		return suggestionsList.map((item, index) => {
			const alreadySelected = !!selectedItems?.find(
				(selectedSuggestion) => selectedSuggestion.value === item.value,
			);

			if (selectedItems?.length && excludeSelected && alreadySelected) {
				return null;
			}

			return (
				<S.Suggestion
					key={item.value}
					{...getItemProps({
						index,
						item,
						key: item.value,
					})}
					onMouseOver={() => setHighlightedItem(item)}
					onMouseLeave={() => setHighlightedItem(undefined)}
					highlighted={highlightedItem && highlightedItem?.value === item.value}
					onClick={() => onSelectedSuggestion(item)}
				>
					{item.label}
				</S.Suggestion>
			);
		});
	};

	const renderGroupedItems = (getItemProps) => {
		return groupedSuggestions.map((group) => {
			return (
				<>
					<p className={"group-title"}>{group.groupName}</p>
					{renderItems(getItemProps, group.suggestions)}
				</>
			);
		});
	};

	const renderEndInputAddon = () => {
		if (endInputAddon) {
			return endInputAddon;
		}
		if (isLoading) {
			return (
				<InputAddon position="end">
					<Spinner size={SpinnerSize.xSmall} />
				</InputAddon>
			);
		}
		return null;
	};

	return (
		<Downshift
			inputValue={inputValue}
			selectedItem={selectedItem}
			onChange={onSelectedValueChange}
			itemToString={itemToString}
			onInputValueChange={onInputValueChange}
			onStateChange={onStateChange}
			defaultIsOpen={defaultIsOpen}
			initialIsOpen={initialIsOpen}
			stateReducer={stateReducer}
		>
			{({
				getInputProps,
				getItemProps,
				getMenuProps,
				getRootProps,
				isOpen,
				openMenu,
			}) => {
				return (
					<Div
						position="relative"
						{...getRootProps({ refKey: "elRef" })}
						{...styles?.root}
					>
						<TextInput
							{...getInputProps()}
							endInputAddon={renderEndInputAddon()}
							disabled={isInputDisabled}
							label={label}
							styles={{
								input: { ...styles?.input },
								label: { ...styles?.label },
							}}
							onFocus={() => openOnFocus && openMenu()}
							size={size}
							value={inputValue}
						/>
						{isOpen &&
						(canOpen === undefined || canOpen) &&
						!(closeOnLoading && isLoading) ? (
							<S.SuggestionsContainer
								testId="suggestions-container"
								{...styles?.suggestions}
							>
								<S.MenuUl {...getMenuProps}>
									{isGroupedSuggestions
										? renderGroupedItems(getItemProps)
										: renderItems(getItemProps, suggestions)}
									{!items.length && (
										<S.Suggestion
											{...getItemProps({
												key: 1,
												item: null,
											})}
										>
											{emptyPlaceholder}
										</S.Suggestion>
									)}
								</S.MenuUl>
							</S.SuggestionsContainer>
						) : null}
					</Div>
				);
			}}
		</Downshift>
	);
};

export default Object.assign(VeBoxAutoSuggest, { Item: S.Suggestion });
